import React from 'react';
import { X, Plus } from 'lucide-react';
import MathText from '../../VueQuestion/MathText';

/**
 * Panneau détail PARTAGÉ des contenus (savoir-faires + attendus) — maquette
 * validée prof 2026-08-25. Un seul langage d'interaction sur la frise :
 * tout compteur cliquable ouvre CE panneau, qu'il vienne de la pill d'une
 * séquence chargée (> 4 contenus) ou d'une chip-séance du rail des rituels
 * d'automatismes. Même moule visuel qu'ActivityListModal (overlay + carte
 * blanche, header dégradé bleu).
 */
const PanneauContenus = ({ isOpen, onClose, activite, rubriques, onAddSavoir, statsPour }) => {
    if (!isOpen || !activite) return null;

    const savoirFaires = activite.savoir_faires_details || [];
    // La rubrique de chaque attendu (objectif / automatisme) est résolue
    // depuis le référentiel (prop `rubriques`), car attendus_details ne la
    // porte pas toujours — un rituel peut embarquer les DEUX rubriques
    // (retour prof 2026-08-25) : les badges les différencient, objectifs
    // affichés d'abord.
    const attendus = (activite.attendus_details || []).map((att) => ({
        ...att,
        _auto: (att.rubrique || rubriques?.get(att.id)) === 'automatisme',
    })).sort((a, b) => (a._auto === b._auto ? 0 : a._auto ? 1 : -1));
    const total = savoirFaires.length + attendus.length;

    // Le panneau parle la langue de ce qui l'ouvre (retour prof
    // 2026-08-25) : bleu pour une séquence, ÉMERAUDE pour un rituel
    // d'automatismes, ROSE pour une résolution de problèmes — la couleur
    // du rail correspondant. Classes en clair (purge JIT).
    const THEMES_PANNEAU = {
        AUTOMATISME: {
            header: 'from-emerald-500 to-emerald-600',
            prefixe: '⚡ Rituel d\'automatismes · ',
            pied: 'border-emerald-300 bg-emerald-50/60 hover:bg-emerald-50 hover:border-emerald-400 text-emerald-600 hover:text-emerald-700',
        },
        RESOLUTION: {
            header: 'from-rose-500 to-rose-600',
            prefixe: '🧩 Résolution de problèmes · ',
            pied: 'border-rose-300 bg-rose-50/60 hover:bg-rose-50 hover:border-rose-400 text-rose-600 hover:text-rose-700',
        },
    };
    const theme = THEMES_PANNEAU[activite.type] || {
        header: 'from-blue-500 to-blue-600',
        prefixe: '',
        pied: 'border-blue-300 bg-blue-50/60 hover:bg-blue-50 hover:border-blue-400 text-blue-500 hover:text-blue-600',
    };

    const formatDate = (iso) => {
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    const badgeDe = (item, type) => {
        if (type !== 'attendu') return { classes: 'bg-violet-100 text-violet-700', label: 'SAVOIR-FAIRE' };
        return item._auto
            ? { classes: 'bg-emerald-100 text-emerald-700', label: '⚡ AUTOMATISME' }
            : { classes: 'bg-blue-100 text-blue-700', label: 'OBJECTIF' };
    };

    const rangee = (item, type, idx) => {
        const badge = badgeDe(item, type);
        // Acquisition (optionnelle, vue classe) : `statsPour` calcule le
        // taux depuis les résultats des élèves — même seuils/couleurs que
        // les satellites de la frise. Absente (vue niveau) : rien.
        const stats = statsPour ? statsPour(item, type === 'attendu' ? 'attendu' : 'savoir-faire') : null;
        return (
        <div
            key={`${type}-${item.id || idx}`}
            className="flex items-start gap-2.5 p-3 rounded-xl border border-slate-200 bg-white shadow-sm"
        >
            <span
                className={`flex-shrink-0 mt-0.5 px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider ${badge.classes}`}
            >
                {badge.label}
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] text-slate-700 leading-snug">
                    <MathText>{item.nom || item.name || item.titre || item.description}</MathText>
                </p>
                {stats ? (
                    <div className="mt-2">
                        <div className="flex justify-between items-center text-[10px] mb-1">
                            <span className="text-slate-500">Acquisition</span>
                            <span className={`font-bold ${
                                stats.percentage >= 75 ? 'text-emerald-600' :
                                stats.percentage >= 50 ? 'text-amber-600' :
                                'text-red-500'
                            }`}>
                                {stats.percentage}% ({stats.acquired}/{stats.total})
                            </span>
                        </div>
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                    stats.percentage >= 75 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' :
                                    stats.percentage >= 50 ? 'bg-gradient-to-r from-amber-400 to-amber-500' :
                                    'bg-gradient-to-r from-red-400 to-red-500'
                                }`}
                                style={{ width: `${stats.percentage}%` }}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
        );
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-2 sm:p-4">
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Header */}
                <div className={`relative px-5 py-4 text-white bg-gradient-to-br ${theme.header}`}>
                    <button
                        onClick={onClose}
                        aria-label="Fermer"
                        className="absolute top-3 right-3 w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                    >
                        <X size={15} />
                    </button>
                    <p className="text-[10px] font-extrabold tracking-widest opacity-85 uppercase pr-10">
                        {theme.prefixe}{activite.title}
                    </p>
                    <h3 className="text-lg font-extrabold">
                        {total} savoir{total > 1 ? 's' : ''} et attendu{total > 1 ? 's' : ''}
                    </h3>
                    {activite.start ? (
                        <p className="text-xs font-semibold opacity-85 mt-0.5">
                            {formatDate(activite.start)}{activite.end ? ` → ${formatDate(activite.end)}` : ''}
                        </p>
                    ) : null}
                </div>

                {/* Liste */}
                <div className="p-4 flex flex-col gap-2 max-h-[55vh] overflow-y-auto">
                    {attendus.map((item, idx) => rangee(item, 'attendu', idx))}
                    {savoirFaires.map((item, idx) => rangee(item, 'savoir-faire', idx))}
                    {total === 0 ? (
                        <p className="text-center text-sm text-slate-400 py-6">Aucun contenu rattaché.</p>
                    ) : null}
                </div>

                {/* Pied */}
                {onAddSavoir ? (
                    <div className="px-4 pb-4">
                        <button
                            onClick={() => { onClose(); onAddSavoir(activite); }}
                            className={`w-full py-2.5 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-2 text-sm font-bold ${theme.pied}`}
                        >
                            <Plus size={15} />
                            Ajouter ou retirer des savoirs
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default PanneauContenus;
